import { useState } from 'react';
import { showSystemMessage } from '../../utils/systemMessage';

const ChevronDownIcon = ({ collapsed }) => (
  <svg
    className={`w-5 h-5 text-white transition-transform duration-300 ${
      collapsed ? 'rotate-180' : ''
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const STEP_CARDS = [
  {
    title: 'Complete Assessment',
    desc: 'Learning Style Match or Custom Search',
  },
  {
    title: 'Check Availability',
    desc: 'View tutor calendars and compare times',
  },
  {
    title: 'Get Scheduled',
    desc: 'Request a date and time to secure your session',
  },
];

const POPUP_WARNING =
  'Please ensure pop-up blockers are turned off before launching the Learner Assessment.';

const SECTION_TITLE = 'Book a Session in 3 Easy Steps';

const BookSessionBlueSection = ({
  embedded = false,
  learnerChoices = [],
  onLaunchAssessment,
  showAssessment = false,
}) => {
  const [bookSessionStepsOpen, setBookSessionStepsOpen] = useState(true);
  const [selectedLearnerLabel, setSelectedLearnerLabel] = useState('');
  const maxInner = embedded
    ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
    : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

  const stepsBlock = (
    <>
      {bookSessionStepsOpen ? (
        <>
          <button
            type="button"
            className="mb-4 flex w-full cursor-pointer items-center justify-center gap-3 text-left select-none"
            onClick={() => setBookSessionStepsOpen(false)}
          >
            <div className="min-w-[1rem] flex-1 border-t border-white/40" />
            <h2 className="shrink-0 px-2 text-center text-xl font-semibold text-white sm:text-2xl">
              {SECTION_TITLE}
            </h2>
            <ChevronDownIcon collapsed={bookSessionStepsOpen} />
            <div className="min-w-[1rem] flex-1 border-t border-white/40" />
          </button>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-10">
            {STEP_CARDS.map((step, i) => (
              <div
                key={step.title}
                className="relative flex min-h-[132px] flex-col justify-center rounded-xl bg-white p-8 pt-9 text-center shadow-md sm:p-10"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white font-bold text-brand-medium-blue shadow">
                    {i + 1}
                  </div>
                </div>
                <h3 className="text-base font-bold text-brand-dark-blue sm:text-lg">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-snug text-gray-600 sm:text-base">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setBookSessionStepsOpen(true)}
          aria-label={`Expand ${SECTION_TITLE}`}
          className="flex w-full items-center justify-center rounded-lg bg-white/10 py-2 text-white transition-colors hover:bg-white/20"
        >
          <span className="inline-block -rotate-90">
            <ChevronDownIcon collapsed={false} />
          </span>
        </button>
      )}
    </>
  );

  const assessmentBlock = showAssessment ? (
    <div className="bg-white text-gray-900">
      <div className={`${maxInner} pb-10 pt-2`}>
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-950 sm:text-sm">
          {POPUP_WARNING}
        </div>
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5 shadow-md sm:p-6">
          <h3 className="text-lg font-bold text-brand-dark-blue">Learner Assessment</h3>
          <p className="mt-2 text-sm text-gray-600">
            Start the assessment to match the learner with tutors based on subject needs and learning style.
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-800" htmlFor="booking-learner">
                Select Learner:
              </label>
              <select
                id="booking-learner"
                className="w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-medium-blue focus:outline-none focus:ring-2 focus:ring-brand-medium-blue/30"
                value={selectedLearnerLabel}
                onChange={(e) => setSelectedLearnerLabel(e.target.value)}
              >
                <option value="">Choose learner</option>
                {learnerChoices.map((learner) => (
                  <option key={learner.learnerLabel} value={learner.learnerLabel}>
                    {learner.learnerFirstName || learner.learnerLabel}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!selectedLearnerLabel) {
                  showSystemMessage({ message: 'Please select a learner before starting.', type: 'warning' });
                  return;
                }
                onLaunchAssessment?.(selectedLearnerLabel);
              }}
              className="shrink-0 rounded-lg bg-brand-medium-blue px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-brand-dark-blue"
            >
              Launch Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (embedded) {
    return (
      <>
        <div className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-medium-blue text-white">
          <div className={`${maxInner} py-6 sm:py-8`}>{stepsBlock}</div>
        </div>
        {assessmentBlock}
      </>
    );
  }

  return (
    <>
      <div className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-medium-blue text-white">
        <div className={`${maxInner} pb-8 pt-8`}>{stepsBlock}</div>
      </div>
      {assessmentBlock}
    </>
  );
};

export default BookSessionBlueSection;
